import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/feature/PageTransition';

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('전체');

  useEffect(() => {
    document.title = '삼원테크 | 현장사진 - 전국 물탱크 설치 시공 사례';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 현장사진. 하남시립도서관, 나로호 우주센터, 소록도 병원 등 전국 각지 STS패널형·원통형·라이닝 물탱크 설치 시공 사례를 확인하세요.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/gallery#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/gallery`,
      "name": "삼원테크 현장사진",
      "description": "전국 각지 STS패널형·원통형·라이닝 물탱크 설치 시공 사례 갤러리.",
      "inLanguage": "ko-KR",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { "@type": "ListItem", "position": 2, "name": "현장사진", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/gallery` }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-gallery';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-gallery')?.remove(); };
  }, []);

  const categories = ['전체', 'STS패널형', 'STS원통형', '라이닝'];

  const galleryItems = [
    {
      id: 1,
      slug: 'hanam-library',
      category: 'STS패널형',
      title: '00도서관',
      location: '광주광역시',
      date: '2024.12',
      capacity: '55톤',
      spec: '4000×4000×3500',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/342cbbb45e64f4581572c66da10ed7f3.jpeg',
    },
    {
      id: 2,
      slug: 'naro-space',
      category: 'STS패널형',
      title: '00우주센터',
      location: '전라남도 고흥군',
      date: '2024.08',
      capacity: '42톤',
      spec: '4000×3000×3500',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/243079514d7fc0060b9590216e9fe487.jpeg',
    },
    {
      id: 3,
      slug: 'sorokdo-hospital',
      category: 'STS패널형',
      title: '00병원',
      location: '전라남도 고흥군 소록도',
      date: '2025.05',
      capacity: '48톤',
      spec: '4000×4000×3000',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/d7e76d2d46b74c605b7e0c4dedc03a85.jpeg',
    },
    {
      id: 5,
      slug: 'hwasun-office',
      category: 'STS원통형',
      title: '00배수지',
      location: '전라남도 화순군',
      date: '2024.08',
      capacity: '90톤',
      spec: '',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/0c181b179731d6c45fa21f83bce6c9c9.jpeg',
    },
    {
      id: 6,
      slug: 'sunchon-univ',
      category: '라이닝',
      title: '00대학교',
      location: '전라남도 순천시',
      date: '2023.09',
      capacity: '',
      spec: '',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/70382b6903f1ae110cffcf4dae0523a0.png',
    },
    {
      id: 7,
      slug: 'baeka-water',
      category: '라이닝',
      title: '00정수장',
      location: '전라남도 화순군',
      date: '2025.10',
      capacity: '',
      spec: '',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/082a9d443dac8357bde286a41d0ff877.jpeg',
    },
  ];

  const filteredItems = activeCategory === '전체'
    ? galleryItems
    : galleryItems.filter(item => item.category === activeCategory);

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                현장사진
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                삼원테크의 다양한 프로젝트 현장을 확인하세요.<br />
                전국 각지에서 진행된 물탱크 설치 및 유지보수 사례입니다.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === category
                      ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredItems.map((item, index) => (
                <motion.div
                  layout
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg transition-all duration-500"
                >
                  <div className="relative w-full h-64 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="px-4 py-2 bg-teal-600 text-white text-xs font-medium rounded-full">
                        {item.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-map-pin-line text-teal-600 mr-2"></i>
                        {item.location}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-calendar-line text-teal-600 mr-2"></i>
                        {item.date}
                      </div>
                      {item.capacity && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-water-flash-line text-teal-600 mr-2"></i>
                          용량: <span className="font-semibold text-teal-700 ml-1">{item.capacity}</span>
                        </div>
                      )}
                      {item.spec && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-ruler-line text-teal-600 mr-2"></i>
                          규격: <span className="font-medium ml-1">{item.spec}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {filteredItems.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-image-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-xl text-gray-500">해당 카테고리의 사진이 없습니다.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-emerald-600">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                프로젝트 문의하기
              </h2>
              <p className="text-lg text-white/90 mb-8">
                귀사의 프로젝트에 대해 상담하고 싶으신가요?<br />
                전문가가 최적의 솔루션을 제안해드립니다.
              </p>
              <a
                href="tel:+82-2-1234-5678"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-bold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                지금 상담하기
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </PageTransition>
  );
};

export default GalleryPage;
